import { Children, FC, isValidElement, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ChevronUp } from '../svgs';

// ─── Types ────────────────────────────────────────────────────────────────────

type CarouselVariant = 'grey' | 'gold' | 'red';

export interface NotificationCarouselProps {
  /** Background colour theme — mirrors Notification variants */
  variant?: CarouselVariant;
  children: ReactNode;
  className?: string;
}

export interface NotificationCarouselSlideProps {
  /**
   * Content of one slide.
   * Mobile: stacks vertically (Notification.Body above Notification.Button).
   * Desktop: side-by-side row.
   */
  children: ReactNode;
  className?: string;
}

// ─── Colour maps ──────────────────────────────────────────────────────────────

const bgMap: Record<CarouselVariant, string> = {
  grey: 'bg-[--notification-bg-grey]',
  gold: 'bg-[--notification-bg-gold]',
  red: 'bg-[--notification-bg-red]',
};

// ─── Slide ────────────────────────────────────────────────────────────────────

/**
 * NotificationCarousel.Slide — one carousel panel.
 *
 * Mobile  : flex-col — body above button.
 * Desktop : flex-row items-center — body left (flex-1), button right.
 *
 * Place Notification.Body and Notification.Button (optional) inside.
 */
const Slide: FC<NotificationCarouselSlideProps> = ({ children, className }) => (
  <div
    className={twMerge(
      // Takes the full width of the sliding track so translateX works
      'w-full flex-shrink-0',
      // Mobile: stacked column; desktop: inline row
      'flex flex-col gap-[--padding-xs]',
      'md:flex-row md:items-center md:gap-[--padding-md]',
      className
    )}
  >
    {children}
  </div>
);

// ─── Compound type ────────────────────────────────────────────────────────────

type NotificationCarouselComponent = FC<NotificationCarouselProps> & {
  Slide: typeof Slide;
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * NotificationCarousel — stateful multi-slide notification banner.
 *
 * Layout (both sizes):
 * ```
 * ┌──────────────────────────────────────────────┐
 * │  [Slide content (Heading + Body + Button)]   │
 * ├──────────────────────────────────────────────┤
 * │  ● ○ ○ ○   (dots)        ←  →  (arrows)     │
 * └──────────────────────────────────────────────┘
 * ```
 *
 * Navigation:
 * - Progress dots on the **left** — active dot is a navy pill, inactive is a white circle.
 * - Prev/Next arrow buttons **grouped on the right** — white circle bg when enabled,
 *   light-grey when disabled.
 *
 * @example
 * <NotificationCarousel variant="grey">
 *   <NotificationCarousel.Slide>
 *     <Notification.Body>
 *       <Notification.Heading>Headline</Notification.Heading>
 *       <Notification.Content>Message.</Notification.Content>
 *     </Notification.Body>
 *     <Notification.Button variant="primary">Learn More</Notification.Button>
 *   </NotificationCarousel.Slide>
 *   <NotificationCarousel.Slide>…</NotificationCarousel.Slide>
 * </NotificationCarousel>
 */
export const NotificationCarousel: NotificationCarouselComponent = ({
  variant = 'grey',
  children,
  className,
}) => {
  const slides = Children.toArray(children).filter(isValidElement);
  const total = slides.length;
  const [current, setCurrent] = useState(0);

  const goTo = (idx: number) => setCurrent(Math.max(0, Math.min(total - 1, idx)));
  const isFirst = current === 0;
  const isLast = current === total - 1;

  return (
    <div
      className={twMerge(
        // Outer container is always flex-col (content row + nav row)
        'flex flex-col',
        'gap-[--padding-xs] md:gap-[--padding-sm]',
        'p-[--padding-sm] md:p-[--padding-md]',
        'rounded-[--corner-xs] md:rounded-[--corner-sm] w-full',
        bgMap[variant],
        className
      )}
    >
      {/* ── Slide viewport ─────────────────────────────────────────────────── */}
      <div className="overflow-hidden w-full">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides}
        </div>
      </div>

      {/* ── Navigation (only when there are multiple slides) ────────────────── */}
      {total > 1 && (
        <div className="flex items-center justify-between w-full">
          {/* Progress dots — LEFT */}
          <div className="flex items-center gap-2 md:gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={twMerge(
                  'rounded-full cursor-pointer transition-all duration-200',
                  i === current
                    ? // Active: navy pill
                      'bg-[--carousel-fg] h-1.5 w-5 md:h-2 md:w-6'
                    : // Inactive: white circle
                      'bg-white size-1.5 md:size-2'
                )}
              />
            ))}
          </div>

          {/* Arrow buttons — RIGHT (grouped together) */}
          <div className="flex items-center gap-3">
            {/* Prev */}
            <button
              type="button"
              onClick={() => goTo(current - 1)}
              disabled={isFirst}
              aria-label="Previous slide"
              className={twMerge(
                // Circle container: 24px small / 32px large
                'flex items-center justify-center rounded-full',
                'size-6 p-[4px] md:size-8 md:p-[4px]',
                'cursor-pointer transition-colors',
                isFirst ? 'bg-[--carousel-disabled-bg] cursor-not-allowed' : 'bg-white'
              )}
            >
              {/* ChevronUp rotated −90° = pointing left */}
              <ChevronUp className="size-4 md:size-6 -rotate-90" />
            </button>

            {/* Next */}
            <button
              type="button"
              onClick={() => goTo(current + 1)}
              disabled={isLast}
              aria-label="Next slide"
              className={twMerge(
                'flex items-center justify-center rounded-full',
                'size-6 p-[4px] md:size-8 md:p-[4px]',
                'cursor-pointer transition-colors',
                isLast ? 'bg-[--carousel-disabled-bg] cursor-not-allowed' : 'bg-white'
              )}
            >
              {/* ChevronUp rotated 90° = pointing right */}
              <ChevronUp className="size-4 md:size-6 rotate-90" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

NotificationCarousel.Slide = Slide;
